import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  computePatientSavings,
  computeRegularTotalValue,
  defaultIsSellableForKind,
  isValidPackageQuantity,
  isValidValidityMonths,
  qInventoryItemById,
  qInventoryMedicineDetailsByItem,
  qInventoryPackageItems,
  qPackageChildCandidates,
  validatePackageChild,
  type InventoryItemRow,
  type InventoryMedicineDetailRow,
  type PackageChildCandidateRow,
  type PackageIncludedItemRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { type ShellOutletContext } from '../../shell/shared';
import { InventoryPageShell } from './_shared';

// ─── Item type strategy ───

// "Skin Care Product" and "Dental Material" used to live here as item
// types but they overlap with `Product` (and `Supply`) — they are
// modeled as Categories instead, available in CATEGORY_OPTIONS below.
const ITEM_TYPES = [
  'Medicine',
  'Product',
  'Package',
  'Supply',
  'Procedure',
  'Equipment',
  'Consumable',
  'Injectable',
  'Service',
] as const;

type ItemType = (typeof ITEM_TYPES)[number];

// PRD.205 — Product is now selectable alongside Medicine.
// PRD.260 — Package is selectable and builds a bundle of other items.
// Remaining types stay disabled with "Coming soon" labels.
const ENABLED_ITEM_TYPES: readonly ItemType[] = [
  'Medicine',
  'Product',
  'Package',
  'Supply',
  // PRD.264 follow-up — non-physical sellable Service items so they can be
  // bundled into packages (PRD.260) and billed. No stock/batch.
  'Service',
];

const STOCK_TRACKED_TYPES: readonly ItemType[] = [
  'Medicine',
  'Product',
  'Supply',
  'Equipment',
  'Consumable',
  'Injectable',
];

const PRICING_TYPES: readonly ItemType[] = [
  'Medicine',
  'Product',
  'Supply',
  'Procedure',
  'Equipment',
  'Consumable',
  'Injectable',
  'Service',
];

// ─── Local option lists ───

const CATEGORY_OPTIONS = [
  'Medicine',
  'Clinic Supplies',
  'Consumables',
  'Injectables',
  'Skin Care Product',
  'Dental Material',
  'Equipment',
  'Procedure',
  'Service',
];

const STATUS_OPTIONS = ['Active', 'Inactive'];

const DRUG_CLASSIFICATIONS = ['OTC', 'Prescription', 'Controlled', 'Supplement', 'Other'];
const DOSAGE_FORMS = [
  'Tablet',
  'Capsule',
  'Syrup',
  'Cream',
  'Ointment',
  'Injection',
  'Drops',
  'Solution',
  'Other',
];
const ROUTES = ['Oral', 'Topical', 'Injectable', 'Ophthalmic', 'Otic', 'Nasal', 'Other'];

const USAGE_OPTIONS = ['Take', 'Apply', 'Inject', 'Instill', 'Use', 'Other'];
const UNIT_OPTIONS = ['tablet', 'capsule', 'mL', 'mg', 'g', 'drop', 'application', 'vial', 'unit'];
const FREQUENCY_OPTIONS = [
  'Once daily',
  'Twice daily',
  'Three times daily',
  'Every 4 hours',
  'Every 6 hours',
  'Every 8 hours',
  'As needed',
  'As directed',
];
const DURATION_OPTIONS = [
  '1 day',
  '3 days',
  '5 days',
  '7 days',
  '10 days',
  '14 days',
  '30 days',
  'As directed',
];

const PURCHASING_UNIT_OPTIONS = [
  'pcs',
  'box',
  'pack',
  'bottle',
  'tube',
  'vial',
  'ampule',
  'tablet',
  'capsule',
  'mL',
  'mg',
  'g',
  'unit',
];

// ─── Form state ───

interface FormState {
  itemType: ItemType;
  // Basic Details
  name: string;
  sku: string;
  externalId: string;
  category: string;
  tags: string;
  localName: string;
  description: string;
  remarks: string;
  status: string;
  // Pricing
  sellingPrice: string;
  minimumSellingPrice: string;
  costPrice: string;
  taxInclusive: boolean;
  openPricing: boolean;
  // PRD.260 — Package-specific. Validity period in months (blank = none).
  validityMonths: string;
  // Medicine Details
  drugClassification: string;
  brandName: string;
  genericName: string;
  manufacturer: string;
  indication: string;
  strength: string;
  dosageForm: string;
  route: string;
  precaution1: string;
  precaution2: string;
  // Dosage & Instructions
  defaultInstruction: string;
  defaultDosage: string;
  usage: string;
  dose: string;
  doseUnit: string;
  frequency: string;
  duration: string;
  // Unit Conversion
  purchasingUnit: string;
  inventoryUnit: string;
  packSizeQuantity: string;
  // Stock Settings
  location: string;
  trackStock: boolean;
  initialStockQuantity: string;
  lowStockLimitAlert: string;
  minimumOrderQuantity: string;
  inactive: boolean;
  // PRD.234 — Pharmacy workflow capability (Products only).
  dispenseViaPharmacy: boolean;
  // PRD.264 — Supply fields.
  supplyKind: 'Consumable' | 'Expendable';
  isSellable: boolean;
  batchTracked: boolean;
  consignment: boolean;
  // Other Codes
  manufacturingBarcode: string;
  accountingCode: string;
  externalReferenceCode: string;
}

const INITIAL_STATE: FormState = {
  itemType: 'Medicine',
  name: '',
  sku: '',
  externalId: '',
  category: '',
  tags: '',
  localName: '',
  description: '',
  remarks: '',
  status: 'Active',
  sellingPrice: '',
  minimumSellingPrice: '',
  costPrice: '',
  taxInclusive: false,
  openPricing: false,
  validityMonths: '',
  drugClassification: '',
  brandName: '',
  genericName: '',
  manufacturer: '',
  indication: '',
  strength: '',
  dosageForm: '',
  route: '',
  precaution1: '',
  precaution2: '',
  defaultInstruction: '',
  defaultDosage: '',
  usage: '',
  dose: '',
  doseUnit: '',
  frequency: '',
  duration: '',
  purchasingUnit: '',
  inventoryUnit: '',
  packSizeQuantity: '',
  location: '',
  trackStock: true,
  initialStockQuantity: '0',
  lowStockLimitAlert: '10',
  minimumOrderQuantity: '1',
  inactive: false,
  dispenseViaPharmacy: false,
  supplyKind: 'Consumable',
  isSellable: true,
  batchTracked: true,
  consignment: false,
  manufacturingBarcode: '',
  accountingCode: '',
  externalReferenceCode: '',
};

// PRD.260 — A row in the package builder. `qty` is kept as editable text and
// parsed on save; `usualPrice` is the child's current selling price used for
// the value summary. `includedUnitPrice` overrides are not exposed in this
// foundation UI (stored as null) but the column exists for a future PRD.
interface DraftIncludedItem {
  key: string;
  childId: string;
  name: string;
  sku: string;
  itemType: string;
  usualPrice: number;
  qty: string;
}

// ─── Page ───

export function CreateInventoryItemPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { db } = usePowerSyncDb();
  const formatMoney = useFormatMoney();

  // PRD.260 — Edit mode when routed with :id. The item id is stable for the
  // page lifetime so package composition rows can reference it even during
  // a fresh create (before the parent row is inserted).
  const { id: routeItemId } = useParams();
  const isEdit = Boolean(routeItemId);
  const itemId = useMemo(() => routeItemId ?? crypto.randomUUID(), [routeItemId]);

  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // PRD.260 — Package builder state.
  const [includedItems, setIncludedItems] = useState<DraftIncludedItem[]>([]);

  // Load existing rows in edit mode. These queries return empty in create mode
  // (the id does not exist yet), so they are harmless there.
  const { data: itemRows = [] } = usePowerSyncQuery<InventoryItemRow>(qInventoryItemById(itemId));
  const { data: medRows = [] } = usePowerSyncQuery<InventoryMedicineDetailRow>(
    qInventoryMedicineDetailsByItem(itemId),
  );
  const { data: packageRows = [] } = usePowerSyncQuery<PackageIncludedItemRow>(
    qInventoryPackageItems(itemId),
  );

  // Hydrate the scalar form fields once from the loaded item row when editing.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isEdit || hydratedRef.current) return;
    const row = itemRows[0];
    if (!row) return;
    hydratedRef.current = true;
    setForm(hydrateFormState(row, medRows[0] ?? null));
  }, [isEdit, itemRows, medRows]);

  // Hydrate included items from their own query. This must NOT share the
  // scalar-form one-shot guard above: the item row and the package-items query
  // resolve independently, and the package rows usually arrive a tick later. We
  // keep mirroring the DB rows until the user first edits the composition
  // (tracked via userTouchedItemsRef), which avoids the earlier race where an
  // empty package list was locked in before the rows loaded.
  const userTouchedItemsRef = useRef(false);
  useEffect(() => {
    if (!isEdit || userTouchedItemsRef.current) return;
    setIncludedItems(
      packageRows.map((p) => ({
        key: p.id,
        childId: p.child_inventory_item_id,
        name: p.child_item_name ?? '(Unknown item)',
        sku: p.child_sku ?? '',
        itemType: p.child_item_type ?? '',
        usualPrice: p.child_selling_price ?? 0,
        qty: String(p.quantity ?? 1),
      })),
    );
  }, [isEdit, packageRows]);

  // Wrap the setter so any user edit stops the DB→state mirroring above.
  const handleIncludedItemsChange = (next: DraftIncludedItem[]) => {
    userTouchedItemsRef.current = true;
    setIncludedItems(next);
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleCancel = () => navigate('/inventory/items');

  const isPackage = form.itemType === 'Package';
  const isSupply = form.itemType === 'Supply';
  const isService = form.itemType === 'Service';

  const handleSave = async () => {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.itemType) nextErrors.itemType = 'Item Type is required.';
    if (!form.name.trim()) nextErrors.name = 'Item Name is required.';
    if (!form.sku.trim()) nextErrors.sku = 'SKU / Item Code is required.';
    // PRD.260 — Validity period must be null or a whole number > 0.
    if (isPackage && form.validityMonths.trim()) {
      if (!isValidValidityMonths(Number(form.validityMonths))) {
        nextErrors.validityMonths = 'Validity Period must be a whole number greater than 0.';
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    // PRD.205 / PRD.260 — Medicine, Product, and Package can be saved. Other
    // types are still disabled in the dropdown but defended here for safety.
    if (
      form.itemType !== 'Medicine' &&
      form.itemType !== 'Product' &&
      form.itemType !== 'Package' &&
      form.itemType !== 'Supply' &&
      form.itemType !== 'Service'
    ) {
      setSaveError('This item type cannot be saved yet.');
      return;
    }

    // PRD.260 — Package composition rules. An active package must include at
    // least one item; every included quantity must be > 0.
    if (isPackage) {
      const packageActive = form.status === 'Active' && !form.inactive;
      if (packageActive && includedItems.length === 0) {
        setSaveError('Add at least one included item before saving this package.');
        return;
      }
      for (const it of includedItems) {
        if (!isValidPackageQuantity(Number(it.qty))) {
          setSaveError('Quantity must be greater than 0 for all included items.');
          return;
        }
      }
    }

    if (!db) {
      setSaveError('Local database not ready. Please try again in a moment.');
      return;
    }
    if (!orgId) {
      setSaveError('Missing organization context. Please sign in again.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const now = new Date().toISOString();
      // DB stores item_type lowercase ('medicine' / 'product' / 'package').
      const isProductSave = form.itemType === 'Product';
      const isMedicineSave = form.itemType === 'Medicine';
      const itemTypeColumn = isPackage
        ? 'package'
        : isSupply
          ? 'supply'
          : isService
            ? 'service'
            : isProductSave
              ? 'product'
              : 'medicine';

      // PRD.264 — Supply behavior fields. Non-supply items stay sellable
      // (is_sellable = 1) so existing billing pickers keep showing them; they
      // are never batch-tracked or consignment by this form.
      const supplyUsageType = isSupply ? form.supplyKind.toLowerCase() : null;
      const isSellableCol = isSupply ? (form.isSellable ? 1 : 0) : 1;
      const batchTrackedCol = isSupply && form.batchTracked ? 1 : 0;
      const consignmentCol = isSupply && form.consignment ? 1 : 0;

      // PRD.260 / PRD.264 — Packages and Services are never physical stock:
      // force stock fields off so the list and stock logic never treat them as
      // trackable.
      const nonPhysical = isPackage || isService;
      const trackStock = nonPhysical ? 0 : form.trackStock ? 1 : 0;
      const packSize = nonPhysical ? 1 : parseIntOrDefault(form.packSizeQuantity, 1);
      const initialStock = nonPhysical ? 0 : parseIntOrDefault(form.initialStockQuantity, 0);
      const lowStock = nonPhysical ? 0 : parseIntOrDefault(form.lowStockLimitAlert, 10);
      const minOrder = nonPhysical ? 1 : parseIntOrDefault(form.minimumOrderQuantity, 1);

      // PRD.234 — dispense_via_pharmacy only applies to Products; force to 0
      // for Medicine/Package so a type change can't leave a stale flag set.
      const dispenseViaPharmacy = isProductSave && form.dispenseViaPharmacy ? 1 : 0;

      // PRD.260 — validity_months only meaningful for packages; null otherwise.
      const validityMonths =
        isPackage && form.validityMonths.trim() ? Number.parseInt(form.validityMonths, 10) : null;

      if (isEdit) {
        await db.execute(
          `UPDATE inventory_items SET
            item_type = ?, validity_months = ?, item_name = ?, sku = ?,
            external_id = ?, category = ?, tags = ?, local_name = ?, description = ?, remarks = ?, status = ?,
            selling_price = ?, minimum_selling_price = ?, cost_price = ?, tax_inclusive = ?, open_pricing = ?,
            purchasing_unit = ?, inventory_unit = ?, pack_size_quantity = ?,
            track_stock = ?, initial_stock_quantity = ?, low_stock_limit = ?, minimum_order_quantity = ?, inactive = ?,
            dispense_via_pharmacy = ?,
            supply_usage_type = ?, is_sellable = ?, batch_tracked = ?, consignment = ?,
            manufacturing_barcode = ?, accounting_code = ?, external_reference_code = ?,
            updated_at = ?
          WHERE id = ?`,
          [
            itemTypeColumn,
            validityMonths,
            form.name.trim(),
            form.sku.trim(),
            form.externalId.trim() || null,
            form.category || null,
            form.tags.trim() || null,
            form.localName.trim() || null,
            form.description.trim() || null,
            form.remarks.trim() || null,
            form.status || 'Active',
            parseFloatOrZero(form.sellingPrice),
            parseFloatOrZero(form.minimumSellingPrice),
            parseFloatOrZero(form.costPrice),
            form.taxInclusive ? 1 : 0,
            form.openPricing ? 1 : 0,
            form.purchasingUnit || null,
            form.inventoryUnit || null,
            packSize,
            trackStock,
            initialStock,
            lowStock,
            minOrder,
            form.inactive ? 1 : 0,
            dispenseViaPharmacy,
            supplyUsageType,
            isSellableCol,
            batchTrackedCol,
            consignmentCol,
            form.manufacturingBarcode.trim() || null,
            form.accountingCode.trim() || null,
            form.externalReferenceCode.trim() || null,
            now,
            itemId,
          ],
        );
      } else {
        await db.execute(
          `INSERT INTO inventory_items (
            id, org_id, item_type, validity_months, item_name, sku,
            external_id, category, tags, local_name, description, remarks, status,
            image_url,
            selling_price, minimum_selling_price, cost_price, tax_inclusive, open_pricing,
            purchasing_unit, inventory_unit, pack_size_quantity,
            track_stock, initial_stock_quantity, low_stock_limit, minimum_order_quantity, inactive,
            dispense_via_pharmacy,
            supply_usage_type, is_sellable, batch_tracked, consignment,
            manufacturing_barcode, accounting_code, external_reference_code,
            created_at, updated_at
          ) VALUES (
            ?, ?, ?, ?, ?, ?,
            ?, ?, ?, ?, ?, ?, ?,
            ?,
            ?, ?, ?, ?, ?,
            ?, ?, ?,
            ?, ?, ?, ?, ?,
            ?,
            ?, ?, ?, ?,
            ?, ?, ?,
            ?, ?
          )`,
          [
            itemId,
            orgId,
            itemTypeColumn,
            validityMonths,
            form.name.trim(),
            form.sku.trim(),
            form.externalId.trim() || null,
            form.category || null,
            form.tags.trim() || null,
            form.localName.trim() || null,
            form.description.trim() || null,
            form.remarks.trim() || null,
            form.status || 'Active',
            null, // image_url — real upload in a later PRD
            parseFloatOrZero(form.sellingPrice),
            parseFloatOrZero(form.minimumSellingPrice),
            parseFloatOrZero(form.costPrice),
            form.taxInclusive ? 1 : 0,
            form.openPricing ? 1 : 0,
            form.purchasingUnit || null,
            form.inventoryUnit || null,
            packSize,
            trackStock,
            initialStock,
            lowStock,
            minOrder,
            form.inactive ? 1 : 0,
            dispenseViaPharmacy,
            supplyUsageType,
            isSellableCol,
            batchTrackedCol,
            consignmentCol,
            form.manufacturingBarcode.trim() || null,
            form.accountingCode.trim() || null,
            form.externalReferenceCode.trim() || null,
            now,
            now,
          ],
        );
      }

      // PRD.205 — Medicine details live in a sibling table. Product/Package
      // saves use only common inventory fields for v1.
      if (isMedicineSave) {
        const existingDetail = medRows[0];
        if (isEdit && existingDetail) {
          await db.execute(
            `UPDATE inventory_medicine_details SET
              drug_classification = ?, brand_name = ?, generic_name = ?, manufacturer = ?, indication = ?,
              strength = ?, dosage_form = ?, route = ?, precaution_1 = ?, precaution_2 = ?,
              default_instruction = ?, default_dosage = ?, usage = ?, dose = ?, dose_unit = ?, frequency = ?, duration = ?,
              updated_at = ?
            WHERE inventory_item_id = ?`,
            [
              form.drugClassification || null,
              form.brandName.trim() || null,
              form.genericName.trim() || null,
              form.manufacturer.trim() || null,
              form.indication.trim() || null,
              form.strength.trim() || null,
              form.dosageForm || null,
              form.route || null,
              form.precaution1.trim() || null,
              form.precaution2.trim() || null,
              form.defaultInstruction.trim() || null,
              form.defaultDosage.trim() || null,
              form.usage || null,
              form.dose.trim() || null,
              form.doseUnit || null,
              form.frequency || null,
              form.duration || null,
              now,
              itemId,
            ],
          );
        } else {
          await db.execute(
            `INSERT INTO inventory_medicine_details (
              id, inventory_item_id,
              drug_classification, brand_name, generic_name, manufacturer, indication,
              strength, dosage_form, route, precaution_1, precaution_2,
              default_instruction, default_dosage, usage, dose, dose_unit, frequency, duration,
              created_at, updated_at
            ) VALUES (
              ?, ?,
              ?, ?, ?, ?, ?,
              ?, ?, ?, ?, ?,
              ?, ?, ?, ?, ?, ?, ?,
              ?, ?
            )`,
            [
              crypto.randomUUID(),
              itemId,
              form.drugClassification || null,
              form.brandName.trim() || null,
              form.genericName.trim() || null,
              form.manufacturer.trim() || null,
              form.indication.trim() || null,
              form.strength.trim() || null,
              form.dosageForm || null,
              form.route || null,
              form.precaution1.trim() || null,
              form.precaution2.trim() || null,
              form.defaultInstruction.trim() || null,
              form.defaultDosage.trim() || null,
              form.usage || null,
              form.dose.trim() || null,
              form.doseUnit || null,
              form.frequency || null,
              form.duration || null,
              now,
              now,
            ],
          );
        }
      }

      // PRD.260 — Replace package composition. Soft-delete every existing
      // active row, then insert the current list. Sequential db.execute keeps
      // one PowerSync CRUD entry per row so the change syncs. Ordering matters:
      // the soft-delete must run before re-inserting the same child, or the
      // partial unique index (org, package, child) WHERE deleted_at IS NULL
      // would reject the new active row.
      if (isPackage) {
        for (const p of packageRows) {
          await db.execute(
            `UPDATE inventory_package_items SET deleted_at = ?, updated_at = ? WHERE id = ?`,
            [now, now, p.id],
          );
        }
        for (let i = 0; i < includedItems.length; i++) {
          const it = includedItems[i];
          await db.execute(
            `INSERT INTO inventory_package_items (
              id, org_id, package_inventory_item_id, child_inventory_item_id,
              quantity, included_unit_price, sort_order,
              created_at, updated_at, deleted_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
            [
              crypto.randomUUID(),
              orgId,
              itemId,
              it.childId,
              parseFloatOrZero(it.qty),
              null,
              i,
              now,
              now,
            ],
          );
        }
      }

      setSavedToast(true);
      window.setTimeout(() => {
        navigate('/inventory/items');
      }, 700);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed. Please try again.';
      setSaveError(msg);
      setSaving(false);
    }
  };

  // PRD.260 — Packages hide all stock/medicine/pricing sections and render
  // their own simplified package fields instead.
  const showStockSections = STOCK_TRACKED_TYPES.includes(form.itemType);
  const showPricing = PRICING_TYPES.includes(form.itemType);
  const isMedicine = form.itemType === 'Medicine';

  return (
    <InventoryPageShell
      title={isEdit ? 'Edit Inventory Item' : 'Create Inventory Item'}
      subtitle={
        isPackage
          ? 'Bundle existing products or services into one sellable package.'
          : 'Set up a medicine, product, supply, procedure, or other billable/stock item.'
      }
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <>
          <Button variant="outline" size="md" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="gradient" size="md" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          background: 'var(--bg-app, #f8fafc)',
        }}
      >
        <div
          style={{
            maxWidth: 960,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {/* Save error banner */}
          {saveError && (
            <div
              role="alert"
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--sp-3) var(--sp-4)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {saveError}
            </div>
          )}

          {/* Item Type */}
          <FormSection
            title="Item Type"
            description="Choose the type of inventory item. The form adjusts based on this selection."
          >
            <LabeledField label="Item Type" required error={errors.itemType}>
              <ItemTypeSelect
                value={form.itemType}
                enabled={ENABLED_ITEM_TYPES}
                onChange={(v) => update('itemType', v)}
              />
            </LabeledField>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              {isPackage
                ? 'A package bundles existing products or services into one sellable item.'
                : 'Medicine, Product, and Package items can be saved. Other inventory types are coming soon.'}
            </p>
          </FormSection>

          {/* Basic Details */}
          <FormSection title="Basic Details" description="Identifying details for this item.">
            <TwoCol>
              <LabeledField
                label={isPackage ? 'Package Name' : 'Item Name'}
                required
                error={errors.name}
              >
                <TextInput
                  value={form.name}
                  onChange={(v) => update('name', v)}
                  placeholder={isPackage ? 'e.g., Acne Starter Package' : 'e.g., Paracetamol 500mg'}
                />
              </LabeledField>
              <LabeledField
                label={isPackage ? 'Package Code / Given ID' : 'SKU / Item Code'}
                required
                error={errors.sku}
              >
                <TextInput
                  value={form.sku}
                  onChange={(v) => update('sku', v)}
                  placeholder={isPackage ? 'e.g., PKG-ACNE-01' : 'e.g., MED-PCM-500'}
                />
              </LabeledField>
            </TwoCol>
            <TwoCol>
              <LabeledField label="Given ID / External ID">
                <TextInput
                  value={form.externalId}
                  onChange={(v) => update('externalId', v)}
                  placeholder="Optional — for imports / legacy"
                />
              </LabeledField>
              <LabeledField label="Category">
                <Select
                  value={form.category}
                  options={['', ...CATEGORY_OPTIONS]}
                  onChange={(v) => update('category', v)}
                  placeholder="Select category"
                />
              </LabeledField>
            </TwoCol>
            <TwoCol>
              <LabeledField label="Tags">
                <TextInput
                  value={form.tags}
                  onChange={(v) => update('tags', v)}
                  placeholder="Comma-separated tags"
                />
              </LabeledField>
              <LabeledField label="Local Name">
                <TextInput
                  value={form.localName}
                  onChange={(v) => update('localName', v)}
                  placeholder="Optional — local language name"
                />
              </LabeledField>
            </TwoCol>
            <LabeledField label="Description">
              <Textarea
                value={form.description}
                onChange={(v) => update('description', v)}
                placeholder="Short description of the item"
              />
            </LabeledField>
            <LabeledField label="Remarks">
              <Textarea
                value={form.remarks}
                onChange={(v) => update('remarks', v)}
                placeholder="Internal notes"
              />
            </LabeledField>
            <LabeledField label="Status">
              <Select
                value={form.status}
                options={STATUS_OPTIONS}
                onChange={(v) => update('status', v)}
              />
            </LabeledField>
          </FormSection>

          {/* PRD.260 — Package-only sections. Simple package builder that
              hides technical stock/medicine settings. */}
          {isPackage && (
            <>
              <FormSection
                title="Package Details"
                description="Pricing and validity for this package."
              >
                <TwoCol>
                  <LabeledField label="Package Price">
                    <TextInput
                      value={form.sellingPrice}
                      onChange={(v) => update('sellingPrice', v)}
                      placeholder="0.00"
                      type="number"
                    />
                  </LabeledField>
                  <LabeledField label="Validity Period" error={errors.validityMonths}>
                    <TextInput
                      value={form.validityMonths}
                      onChange={(v) => update('validityMonths', v)}
                      placeholder="e.g., 12 (months)"
                      type="number"
                    />
                  </LabeledField>
                </TwoCol>
                <p
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Used later when a patient buys this package.
                </p>
                <CheckboxRow
                  label="Inactive"
                  description="Hide this package from new transactions."
                  checked={form.inactive}
                  onChange={(v) => update('inactive', v)}
                />
              </FormSection>

              <IncludedItemsSection
                orgId={orgId}
                packageId={itemId}
                items={includedItems}
                onChange={handleIncludedItemsChange}
                packagePrice={parseFloatNumber(form.sellingPrice)}
                formatMoney={formatMoney}
              />
            </>
          )}

          {/* Image */}
          {!isPackage && (
            <FormSection
              title="Image"
              description="Optional product image. Real upload storage will be added in a later PRD."
            >
              <ImagePlaceholder />
            </FormSection>
          )}

          {/* Pricing */}
          {showPricing && (
            <FormSection title="Pricing" description="Used for billing and dispensing.">
              <TwoCol>
                <LabeledField label="Selling Price">
                  <TextInput
                    value={form.sellingPrice}
                    onChange={(v) => update('sellingPrice', v)}
                    placeholder="0.00"
                    type="number"
                  />
                </LabeledField>
                <LabeledField label="Minimum Selling Price">
                  <TextInput
                    value={form.minimumSellingPrice}
                    onChange={(v) => update('minimumSellingPrice', v)}
                    placeholder="0.00"
                    type="number"
                  />
                </LabeledField>
              </TwoCol>
              <TwoCol>
                <LabeledField label="Cost Price">
                  <TextInput
                    value={form.costPrice}
                    onChange={(v) => update('costPrice', v)}
                    placeholder="0.00"
                    type="number"
                  />
                </LabeledField>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                  <CheckboxRow
                    label="Tax Inclusive"
                    checked={form.taxInclusive}
                    onChange={(v) => update('taxInclusive', v)}
                  />
                  <CheckboxRow
                    label="Open Pricing"
                    description="Final price can be entered manually during billing."
                    checked={form.openPricing}
                    onChange={(v) => update('openPricing', v)}
                  />
                </div>
              </TwoCol>
            </FormSection>
          )}

          {/* Type-specific section (not shown for Package). */}
          {isPackage ? null : isMedicine ? (
            <>
              <FormSection
                title="Medicine Details"
                description="Pharmacology and identification fields specific to medicines."
              >
                <TwoCol>
                  <LabeledField label="Drug Classification">
                    <Select
                      value={form.drugClassification}
                      options={['', ...DRUG_CLASSIFICATIONS]}
                      onChange={(v) => update('drugClassification', v)}
                      placeholder="Select classification"
                    />
                  </LabeledField>
                  <LabeledField label="Brand Name">
                    <TextInput value={form.brandName} onChange={(v) => update('brandName', v)} />
                  </LabeledField>
                </TwoCol>
                <TwoCol>
                  <LabeledField label="Generic Name">
                    <TextInput
                      value={form.genericName}
                      onChange={(v) => update('genericName', v)}
                    />
                  </LabeledField>
                  <LabeledField label="Manufacturer">
                    <TextInput
                      value={form.manufacturer}
                      onChange={(v) => update('manufacturer', v)}
                    />
                  </LabeledField>
                </TwoCol>
                <LabeledField label="Indication">
                  <TextInput
                    value={form.indication}
                    onChange={(v) => update('indication', v)}
                    placeholder="e.g., Mild to moderate pain, fever"
                  />
                </LabeledField>
                <TwoCol>
                  <LabeledField label="Strength">
                    <TextInput
                      value={form.strength}
                      onChange={(v) => update('strength', v)}
                      placeholder="e.g., 500mg"
                    />
                  </LabeledField>
                  <LabeledField label="Dosage Form">
                    <Select
                      value={form.dosageForm}
                      options={['', ...DOSAGE_FORMS]}
                      onChange={(v) => update('dosageForm', v)}
                      placeholder="Select dosage form"
                    />
                  </LabeledField>
                </TwoCol>
                <TwoCol>
                  <LabeledField label="Route">
                    <Select
                      value={form.route}
                      options={['', ...ROUTES]}
                      onChange={(v) => update('route', v)}
                      placeholder="Select route"
                    />
                  </LabeledField>
                  <div />
                </TwoCol>
                <TwoCol>
                  <LabeledField label="Precaution 1">
                    <TextInput
                      value={form.precaution1}
                      onChange={(v) => update('precaution1', v)}
                      placeholder="Optional"
                    />
                  </LabeledField>
                  <LabeledField label="Precaution 2">
                    <TextInput
                      value={form.precaution2}
                      onChange={(v) => update('precaution2', v)}
                      placeholder="Optional"
                    />
                  </LabeledField>
                </TwoCol>
              </FormSection>

              <FormSection
                title="Dosage & Instructions"
                description="Default dispensing instructions used as a starting point during prescribing."
              >
                <LabeledField label="Default Instruction">
                  <TextInput
                    value={form.defaultInstruction}
                    onChange={(v) => update('defaultInstruction', v)}
                    placeholder="e.g., Take 1 tablet every 6 hours as needed for pain"
                  />
                </LabeledField>
                <LabeledField label="Default Dosage">
                  <TextInput
                    value={form.defaultDosage}
                    onChange={(v) => update('defaultDosage', v)}
                    placeholder="e.g., 500mg"
                  />
                </LabeledField>
                <TwoCol>
                  <LabeledField label="Usage">
                    <Select
                      value={form.usage}
                      options={['', ...USAGE_OPTIONS]}
                      onChange={(v) => update('usage', v)}
                      placeholder="Select usage"
                    />
                  </LabeledField>
                  <LabeledField label="Dose">
                    <TextInput
                      value={form.dose}
                      onChange={(v) => update('dose', v)}
                      placeholder="e.g., 1"
                      type="number"
                    />
                  </LabeledField>
                </TwoCol>
                <TwoCol>
                  <LabeledField label="Unit">
                    <Select
                      value={form.doseUnit}
                      options={['', ...UNIT_OPTIONS]}
                      onChange={(v) => update('doseUnit', v)}
                      placeholder="Select unit"
                    />
                  </LabeledField>
                  <LabeledField label="Frequency">
                    <Select
                      value={form.frequency}
                      options={['', ...FREQUENCY_OPTIONS]}
                      onChange={(v) => update('frequency', v)}
                      placeholder="Select frequency"
                    />
                  </LabeledField>
                </TwoCol>
                <LabeledField label="Duration">
                  <Select
                    value={form.duration}
                    options={['', ...DURATION_OPTIONS]}
                    onChange={(v) => update('duration', v)}
                    placeholder="Select duration"
                  />
                </LabeledField>
              </FormSection>
            </>
          ) : form.itemType === 'Product' ? (
            <FormSection
              title="Product Details"
              description="Pharmacy workflow capability for this Product."
            >
              {/* PRD.234 — Pharmacy-dispensable Product flag. When on,
                  the Product joins the Pharmacy queue and gets the
                  Ready-for-Pharmacy badge on invoices instead of the
                  Product Release flow. Item type stays 'product' so
                  non-pharmacy product workflows (retail Product Release)
                  keep their domain identity. */}
              <CheckboxRow
                label="Dispense via Pharmacy"
                description="Products with this flag follow the same workflow as Medicines: they enter the Pharmacy queue and are stocked-out via Pharmacy Confirm Dispense (not Product Release)."
                checked={form.dispenseViaPharmacy}
                onChange={(v) => update('dispenseViaPharmacy', v)}
              />
            </FormSection>
          ) : isSupply ? (
            <FormSection
              title="Supply Details"
              description="How this supply is classified, sold, and stocked."
            >
              <TwoCol>
                <LabeledField label="Supply Kind">
                  <Select
                    value={form.supplyKind}
                    options={['Consumable', 'Expendable']}
                    onChange={(v) => {
                      const kind = v as 'Consumable' | 'Expendable';
                      update('supplyKind', kind);
                      // PRD.264 §6 — default sellability from kind on NEW items
                      // only; never force-change a saved item's is_sellable.
                      if (!isEdit) {
                        update(
                          'isSellable',
                          defaultIsSellableForKind(
                            kind === 'Consumable' ? 'consumable' : 'expendable',
                          ),
                        );
                      }
                    }}
                  />
                </LabeledField>
                <div />
              </TwoCol>
              <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                Consumable — usually charged to the patient. Expendable — usually internal clinic
                supply.
              </p>
              <CheckboxRow
                label="Sellable"
                description="Sellable supplies appear in the invoice/billing item picker. Expendables are internal-use by default."
                checked={form.isSellable}
                onChange={(v) => update('isSellable', v)}
              />
              <CheckboxRow
                label="Batch Tracked"
                description="Requires selecting a batch/expiry when the supply is used or released."
                checked={form.batchTracked}
                onChange={(v) => update('batchTracked', v)}
              />
              {form.supplyKind === 'Consumable' && (
                <CheckboxRow
                  label="Consignment"
                  description="Stock owned by a supplier until used. Stored for reference only in this version."
                  checked={form.consignment}
                  onChange={(v) => update('consignment', v)}
                />
              )}
            </FormSection>
          ) : (
            <FormSection
              title={`${form.itemType} Details`}
              description="Type-specific fields for this item type will be added in a later PRD."
            >
              <div
                style={{
                  padding: 'var(--sp-6)',
                  border: '1px dashed var(--border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  textAlign: 'center',
                }}
              >
                No additional fields for {form.itemType} yet — common sections still apply.
              </div>
            </FormSection>
          )}

          {/* Unit Conversion */}
          {showStockSections && (
            <FormSection
              title="Inventory Unit Conversion"
              description="Used when the purchasing unit differs from the dispense / inventory unit."
            >
              <ConversionPreview
                purchasingUnit={form.purchasingUnit}
                inventoryUnit={form.inventoryUnit}
                packSizeQuantity={form.packSizeQuantity}
              />
              <TwoCol>
                <LabeledField label="Purchasing Unit">
                  <Select
                    value={form.purchasingUnit}
                    options={['', ...PURCHASING_UNIT_OPTIONS]}
                    onChange={(v) => update('purchasingUnit', v)}
                    placeholder="e.g., box"
                  />
                </LabeledField>
                <LabeledField label="Inventory Unit / Dispense Unit">
                  <Select
                    value={form.inventoryUnit}
                    options={['', ...PURCHASING_UNIT_OPTIONS]}
                    onChange={(v) => update('inventoryUnit', v)}
                    placeholder="e.g., tablet"
                  />
                </LabeledField>
              </TwoCol>
              <LabeledField label="Pack Size Quantity">
                <TextInput
                  value={form.packSizeQuantity}
                  onChange={(v) => update('packSizeQuantity', v)}
                  placeholder="e.g., 100"
                  type="number"
                />
              </LabeledField>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                }}
              >
                Conversion information may be locked after the item is saved because it affects
                stock history.
              </p>
            </FormSection>
          )}

          {/* Stock Settings */}
          {showStockSections && (
            <FormSection
              title="Stock Settings"
              description="Defaults for stock tracking and reorder behavior."
            >
              <TwoCol>
                <LabeledField label="Location">
                  <TextInput
                    value={form.location}
                    onChange={(v) => update('location', v)}
                    placeholder="e.g., Main Pharmacy"
                  />
                </LabeledField>
                <CheckboxRow
                  label="Track Stock"
                  description="Record stock movements for this item."
                  checked={form.trackStock}
                  onChange={(v) => update('trackStock', v)}
                />
              </TwoCol>
              <TwoCol>
                <LabeledField label="Initial Stock Quantity">
                  <TextInput
                    value={form.initialStockQuantity}
                    onChange={(v) => update('initialStockQuantity', v)}
                    type="number"
                  />
                </LabeledField>
                <LabeledField label="Low Stock Limit Alert">
                  <TextInput
                    value={form.lowStockLimitAlert}
                    onChange={(v) => update('lowStockLimitAlert', v)}
                    type="number"
                  />
                </LabeledField>
              </TwoCol>
              <TwoCol>
                <LabeledField label="Minimum Order Quantity">
                  <TextInput
                    value={form.minimumOrderQuantity}
                    onChange={(v) => update('minimumOrderQuantity', v)}
                    type="number"
                  />
                </LabeledField>
                <CheckboxRow
                  label="Inactive"
                  description="Hide this item from new transactions."
                  checked={form.inactive}
                  onChange={(v) => update('inactive', v)}
                />
              </TwoCol>
            </FormSection>
          )}

          {/* Other Codes (hidden for Package — keep the builder simple). */}
          {!isPackage && (
            <FormSection
              title="Other Codes"
              description="Optional codes for barcoding, accounting, and external integrations."
            >
              <TwoCol>
                <LabeledField label="Manufacturing Barcode">
                  <TextInput
                    value={form.manufacturingBarcode}
                    onChange={(v) => update('manufacturingBarcode', v)}
                    placeholder="Optional"
                  />
                </LabeledField>
                <LabeledField label="Accounting Code">
                  <TextInput
                    value={form.accountingCode}
                    onChange={(v) => update('accountingCode', v)}
                    placeholder="Optional"
                  />
                </LabeledField>
              </TwoCol>
              <LabeledField label="External Reference Code">
                <TextInput
                  value={form.externalReferenceCode}
                  onChange={(v) => update('externalReferenceCode', v)}
                  placeholder="Optional"
                />
              </LabeledField>
            </FormSection>
          )}

          {/* Footer actions (mirror header) */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--sp-3)',
              paddingTop: 'var(--sp-2)',
              paddingBottom: 'var(--sp-4)',
            }}
          >
            <Button variant="outline" size="md" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button variant="gradient" size="md" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {savedToast && <SavedToast />}
    </InventoryPageShell>
  );
}

// ─── Helpers ───

function parseIntOrDefault(v: string, fallback: number): number {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
}

function parseFloatOrZero(v: string): string {
  const n = Number.parseFloat(v);
  if (!Number.isFinite(n)) return '0';
  return n.toString();
}

function parseFloatNumber(v: string): number {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

// PRD.260 — Map a lowercase DB item_type to the PascalCase UI value.
function itemTypeLabel(dbType: string | null): ItemType {
  switch ((dbType ?? '').toLowerCase()) {
    case 'package':
      return 'Package';
    case 'product':
      return 'Product';
    case 'supply':
      return 'Supply';
    case 'service':
      return 'Service';
    default:
      return 'Medicine';
  }
}

// PRD.260 — Rehydrate the form from a loaded inventory item (+ optional
// medicine detail row) for edit mode. Booleans arrive as 0/1 from SQLite.
function hydrateFormState(
  row: InventoryItemRow,
  med: InventoryMedicineDetailRow | null,
): FormState {
  const numToStr = (n: number | null | undefined): string =>
    n === null || n === undefined ? '' : String(n);
  return {
    itemType: itemTypeLabel(row.item_type),
    name: row.item_name ?? '',
    sku: row.sku ?? '',
    externalId: row.external_id ?? '',
    category: row.category ?? '',
    tags: row.tags ?? '',
    localName: row.local_name ?? '',
    description: row.description ?? '',
    remarks: row.remarks ?? '',
    status: row.status ?? 'Active',
    sellingPrice: numToStr(row.selling_price),
    minimumSellingPrice: numToStr(row.minimum_selling_price),
    costPrice: numToStr(row.cost_price),
    taxInclusive: row.tax_inclusive === 1,
    openPricing: row.open_pricing === 1,
    validityMonths: numToStr(row.validity_months),
    drugClassification: med?.drug_classification ?? '',
    brandName: med?.brand_name ?? '',
    genericName: med?.generic_name ?? '',
    manufacturer: med?.manufacturer ?? '',
    indication: med?.indication ?? '',
    strength: med?.strength ?? '',
    dosageForm: med?.dosage_form ?? '',
    route: med?.route ?? '',
    precaution1: med?.precaution_1 ?? '',
    precaution2: med?.precaution_2 ?? '',
    defaultInstruction: med?.default_instruction ?? '',
    defaultDosage: med?.default_dosage ?? '',
    usage: med?.usage ?? '',
    dose: med?.dose ?? '',
    doseUnit: med?.dose_unit ?? '',
    frequency: med?.frequency ?? '',
    duration: med?.duration ?? '',
    purchasingUnit: row.purchasing_unit ?? '',
    inventoryUnit: row.inventory_unit ?? '',
    packSizeQuantity: numToStr(row.pack_size_quantity),
    location: '',
    trackStock: row.track_stock === 1,
    initialStockQuantity: numToStr(row.initial_stock_quantity),
    lowStockLimitAlert: numToStr(row.low_stock_limit),
    minimumOrderQuantity: numToStr(row.minimum_order_quantity),
    inactive: row.inactive === 1,
    dispenseViaPharmacy: row.dispense_via_pharmacy === 1,
    supplyKind: row.supply_usage_type === 'expendable' ? 'Expendable' : 'Consumable',
    // is_sellable/batch_tracked may be null on legacy rows → default sensibly.
    isSellable:
      row.is_sellable === null || row.is_sellable === undefined ? true : row.is_sellable === 1,
    batchTracked: row.batch_tracked === 1,
    consignment: row.consignment === 1,
    manufacturingBarcode: row.manufacturing_barcode ?? '',
    accountingCode: row.accounting_code ?? '',
    externalReferenceCode: row.external_reference_code ?? '',
  };
}

// ─── PRD.260 — Included Items builder ───

function IncludedItemsSection({
  orgId,
  packageId,
  items,
  onChange,
  packagePrice,
  formatMoney,
}: {
  orgId: string;
  packageId: string;
  items: DraftIncludedItem[];
  onChange: (next: DraftIncludedItem[]) => void;
  packagePrice: number;
  formatMoney: (value: number | string | null | undefined) => string;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search), 200);
    return () => window.clearTimeout(t);
  }, [search]);

  // Close the results dropdown on outside click or Escape (like the other
  // inventory/invoice pickers).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const { data: candidates = [] } = usePowerSyncQuery<PackageChildCandidateRow>(
    qPackageChildCandidates(orgId, debounced, packageId),
  );

  const existingIds = useMemo(() => new Set(items.map((i) => i.childId)), [items]);
  const results = candidates.filter((c) => !existingIds.has(c.id));

  const addItem = (c: PackageChildCandidateRow) => {
    // PRD.260 — Defend the composition rules even though the search query
    // already excludes packages and the current record. Surfaces the same
    // user-facing messages the PRD lists (§12).
    const reason = validatePackageChild({
      childId: c.id,
      childItemType: c.item_type,
      packageId,
      existingChildIds: items.map((i) => i.childId),
    });
    if (reason) {
      setAddError(reason);
      return;
    }
    setAddError(null);
    onChange([
      ...items,
      {
        key: crypto.randomUUID(),
        childId: c.id,
        name: c.item_name ?? '(Unnamed)',
        sku: c.sku ?? '',
        itemType: c.item_type ?? '',
        usualPrice: c.selling_price ?? 0,
        qty: '1',
      },
    ]);
    setSearch('');
    setDebounced('');
    setOpen(false);
  };

  const updateQty = (key: string, qty: string) => {
    onChange(items.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const removeItem = (key: string) => {
    onChange(items.filter((i) => i.key !== key));
  };

  const regularTotal = computeRegularTotalValue(
    items.map((i) => ({ usualPrice: i.usualPrice, quantity: parseFloatNumber(i.qty) })),
  );
  const savings = computePatientSavings(regularTotal, packagePrice);

  return (
    <FormSection
      title="Included Items"
      description="Products or services bundled into this package."
    >
      {/* Search / add */}
      <div ref={wrapperRef} style={{ position: 'relative' }}>
        <TextInput
          value={search}
          onChange={(v) => {
            setSearch(v);
            setOpen(true);
          }}
          placeholder="Add item or service — search by name or code"
        />
        {open && (search.trim() !== '' || results.length > 0) && (
          <div
            style={{
              position: 'absolute',
              top: 44,
              left: 0,
              right: 0,
              zIndex: 30,
              maxHeight: 260,
              overflowY: 'auto',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.12))',
            }}
          >
            {results.length === 0 ? (
              <div
                style={{
                  padding: 'var(--sp-3) var(--sp-4)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                No matching items.
              </div>
            ) : (
              results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => addItem(c)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: 'var(--sp-3) var(--sp-4)',
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text)',
                      }}
                    >
                      {c.item_name}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                      {typeTitle(c.item_type)} · {c.sku || '—'}
                    </span>
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text)',
                    }}
                  >
                    {formatMoney(c.selling_price)}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {addError && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
          {addError}
        </div>
      )}

      {/* Included items table / empty state */}
      {items.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-6)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            textAlign: 'center',
          }}
        >
          No included items yet. Add products or services that belong to this package.
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <IncludedItemsHeaderRow />
          {items.map((i) => (
            <IncludedItemRow
              key={i.key}
              item={i}
              formatMoney={formatMoney}
              onQtyChange={(v) => updateQty(i.key, v)}
              onRemove={() => removeItem(i.key)}
            />
          ))}
        </div>
      )}

      {/* Value summary */}
      <PackageValueSummary
        regularTotal={regularTotal}
        packagePrice={packagePrice}
        savings={savings}
        formatMoney={formatMoney}
      />
    </FormSection>
  );
}

function typeTitle(t: string | null): string {
  const s = (t ?? '').toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
}

const INCLUDED_GRID = '2fr 1fr 1fr 90px 1fr 70px';

function IncludedItemsHeaderRow() {
  const cell: CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semi)',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: INCLUDED_GRID,
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--bg-surface-2, #f8fafc)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span style={cell}>Item / Service</span>
      <span style={cell}>Type</span>
      <span style={{ ...cell, textAlign: 'right' }}>Usual Price</span>
      <span style={{ ...cell, textAlign: 'right' }}>Qty</span>
      <span style={{ ...cell, textAlign: 'right' }}>Included Value</span>
      <span style={{ ...cell, textAlign: 'right' }}>Actions</span>
    </div>
  );
}

function IncludedItemRow({
  item,
  formatMoney,
  onQtyChange,
  onRemove,
}: {
  item: DraftIncludedItem;
  formatMoney: (value: number | string | null | undefined) => string;
  onQtyChange: (v: string) => void;
  onRemove: () => void;
}) {
  const included = item.usualPrice * (parseFloatNumber(item.qty) || 0);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: INCLUDED_GRID,
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-4)',
        alignItems: 'center',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {item.name}
        </span>
        {item.sku && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {item.sku}
          </span>
        )}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
        {typeTitle(item.itemType)}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)', textAlign: 'right' }}>
        {formatMoney(item.usualPrice)}
      </span>
      <input
        type="number"
        min="0"
        step="any"
        value={item.qty}
        onChange={(e) => onQtyChange(e.target.value)}
        style={{
          height: 34,
          width: '100%',
          padding: '0 var(--sp-2)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          textAlign: 'right',
          fontFamily: 'var(--font-family)',
        }}
      />
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
          textAlign: 'right',
        }}
      >
        {formatMoney(included)}
      </span>
      <span style={{ textAlign: 'right' }}>
        <button
          type="button"
          onClick={onRemove}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--color-danger, #dc2626)',
            fontSize: 'var(--font-size-sm)',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          Remove
        </button>
      </span>
    </div>
  );
}

function PackageValueSummary({
  regularTotal,
  packagePrice,
  savings,
  formatMoney,
}: {
  regularTotal: number;
  packagePrice: number;
  savings: number;
  formatMoney: (value: number | string | null | undefined) => string;
}) {
  const isPriceDifference = savings < 0;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2, #f8fafc)',
        border: '1px solid var(--border)',
      }}
    >
      <SummaryRow label="Regular Total Value" value={formatMoney(regularTotal)} />
      <SummaryRow label="Package Price" value={formatMoney(packagePrice)} />
      <SummaryRow
        label={isPriceDifference ? 'Price difference' : 'Patient Savings'}
        value={formatMoney(Math.abs(savings))}
        emphasize
        tone={isPriceDifference ? 'warn' : 'good'}
      />
    </div>
  );
}

function SummaryRow({
  label,
  value,
  emphasize,
  tone,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
  tone?: 'good' | 'warn';
}) {
  const color =
    tone === 'good'
      ? 'var(--color-success-text, #15803d)'
      : tone === 'warn'
        ? 'var(--color-warning-text, #b45309)'
        : 'var(--text)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: emphasize ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
          color: emphasize ? color : 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: emphasize ? 'var(--font-size-base)' : 'var(--font-size-sm)',
          fontWeight: emphasize ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
          color: emphasize ? color : 'var(--text)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Item Type select with disabled "Coming soon" entries ───

function ItemTypeSelect({
  value,
  enabled,
  onChange,
}: {
  value: ItemType;
  enabled: readonly ItemType[];
  onChange: (v: ItemType) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ItemType)}
      style={{
        height: 40,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        fontSize: 'var(--font-size-base)',
        color: 'var(--text)',
        outline: 'none',
        fontFamily: 'var(--font-family)',
      }}
    >
      {ITEM_TYPES.map((opt) => {
        const isEnabled = enabled.includes(opt);
        return (
          <option key={opt} value={opt} disabled={!isEnabled}>
            {isEnabled ? opt : `${opt} — Coming soon`}
          </option>
        );
      })}
    </select>
  );
}

// ─── Sectioning ───

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            lineHeight: 'var(--line-height-lg)',
          }}
        >
          {title}
        </h2>
        {description && (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

function TwoCol({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

// ─── Field primitives ───

function LabeledField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        {label}
        {required && (
          <span style={{ color: 'var(--color-danger, #dc2626)', marginLeft: 4 }}>*</span>
        )}
      </span>
      {children}
      {error && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-danger, #dc2626)',
            marginTop: 2,
          }}
        >
          {error}
        </span>
      )}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        height: 40,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        fontSize: 'var(--font-size-base)',
        color: 'var(--text)',
        outline: 'none',
        fontFamily: 'var(--font-family)',
      }}
    />
  );
}

function Textarea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      style={{
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        fontSize: 'var(--font-size-base)',
        color: 'var(--text)',
        outline: 'none',
        fontFamily: 'var(--font-family)',
        resize: 'vertical',
        minHeight: 80,
      }}
    />
  );
}

function Select({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 40,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        fontSize: 'var(--font-size-base)',
        color: 'var(--text)',
        outline: 'none',
        fontFamily: 'var(--font-family)',
      }}
    >
      {options.map((opt) => (
        <option key={opt || '__empty'} value={opt}>
          {opt === '' ? (placeholder ?? 'Select...') : opt}
        </option>
      ))}
    </select>
  );
}

function CheckboxRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 'var(--sp-3)',
        alignItems: 'flex-start',
        cursor: 'pointer',
        padding: 'var(--sp-2) 0',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3, accentColor: 'var(--color-primary)' }}
      />
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {label}
        </div>
        {description && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginTop: 2,
            }}
          >
            {description}
          </div>
        )}
      </div>
    </label>
  );
}

// ─── Image placeholder ───

function ImagePlaceholder() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--sp-4)',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: 120,
          height: 120,
          borderRadius: 'var(--radius-md)',
          border: '1px dashed var(--border)',
          background: 'var(--bg-surface-2, #f3f4f6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-sm)',
          flexShrink: 0,
        }}
      >
        Image
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <Button variant="outline" size="md" onClick={() => undefined}>
          Choose file
        </Button>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          PNG or JPG. Real upload storage will be added later.
        </span>
      </div>
    </div>
  );
}

// ─── Conversion preview ───

function ConversionPreview({
  purchasingUnit,
  inventoryUnit,
  packSizeQuantity,
}: {
  purchasingUnit: string;
  inventoryUnit: string;
  packSizeQuantity: string;
}) {
  const hasAll =
    purchasingUnit && inventoryUnit && packSizeQuantity && Number(packSizeQuantity) > 0;
  return (
    <div
      style={{
        padding: 'var(--sp-3) var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2, #f3f4f6)',
        fontSize: 'var(--font-size-sm)',
        color: hasAll ? 'var(--text)' : 'var(--text-muted)',
      }}
    >
      {hasAll
        ? `1 ${purchasingUnit} contains ${packSizeQuantity} ${inventoryUnit}`
        : '1 [Purchasing Unit] contains [Pack Size Quantity] [Inventory Unit]'}
    </div>
  );
}

// ─── Toast ───

function SavedToast() {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        background: 'var(--color-success-bg)',
        color: 'var(--color-success-text)',
        padding: 'var(--sp-3) var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.12))',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
      }}
    >
      Item saved (mock) — returning to Inventory Items.
    </div>
  );
}
